import { useRouter } from 'next/router'

import { Screen } from 'constants/tracking/screens'
import useTranslate from 'hooks/useTranslate'
import Profile from 'pages/Profile/Profile'
import {
  getCurrentUserRequest,
  getProfileUserInfoRequest,
  getUserByIdRequest,
} from 'data/api/requests'
import { ROOT_URL } from 'constants/routes'
import { UserAccountStatus } from 'constants/user'
import { UserThumbnailSize } from 'constants/images'
import { STATIC_ASSETS_URL } from 'constants/index'

import PageHead from '../../../components/Head/PageHead'
import fullPageLayout from '../../../layouts/FullPage'
import { getLayoutServerSideProps } from '../../../layouts/FullPage/server-props'

const redirectToRoot = () => ({
  redirect: {
    destination: ROOT_URL,
    permanent: true,
  },
})

type Props = {
  userId: number
  userName: string
  userPhotoUrl: string
}

const ProfilePage = ({ userId, userName, userPhotoUrl }: Props) => {
  const translate = useTranslate('profile.page')
  // TODO: router query was lifted up from MultipleCollectionsBlock until profile page rails code is cleaned up
  const viewedCollectionId = Number(useRouter().query.collection) || undefined
  const { push } = useRouter()
  const nextPush = (pageURL: string) => push(pageURL, undefined, { shallow: true })

  return (
    <>
      <PageHead title={translate('title', { username: userName })}>
        <meta property="og:title" content={userName} />
        <meta property="og:image" content={userPhotoUrl} />
      </PageHead>
      <Profile userId={userId} viewedCollectionId={viewedCollectionId} nextPush={nextPush} />
    </>
  )
}

export default fullPageLayout<Props>(ProfilePage)

export const getServerSideProps = getLayoutServerSideProps(
  async (context, serverProps) => {
    const { userHash } = context.query
    const userId = typeof userHash === 'string' ? parseInt(userHash, 10) : null

    if (!userId) return redirectToRoot()

    const viewedUser = await getUserByIdRequest(serverProps.api)(userId, { throwError: true })
      .then(response => response.user)
      .catch(() => null)

    if (!viewedUser) return redirectToRoot()

    const currentUser = await getCurrentUserRequest(serverProps.api)({ throwError: true })
      .then(response => response.user)
      .catch(() => null)

    const hasSupportRole = currentUser
      ? await getProfileUserInfoRequest(currentUser.id)(serverProps.api)({
          throwError: true,
        })
          .then(response => response.show_support_functionality)
          .catch(() => false)
      : false

    const isViewingSelf = currentUser?.id === viewedUser.id
    const canViewProfile =
      viewedUser.account_status !== UserAccountStatus.Deleted || isViewingSelf || hasSupportRole

    if (!canViewProfile) {
      return Promise.resolve({
        notFound: true,
      } as const)
    }

    const userPhotoUrl =
      viewedUser.photo?.thumbnails.find(thumb => thumb.type === UserThumbnailSize.X6Large)?.url ||
      `${STATIC_ASSETS_URL}/assets/no-photo/user.png`

    return Promise.resolve({
      props: {
        userId: viewedUser.id,
        userName: viewedUser.login,
        userPhotoUrl,
      },
    })
  },
  {
    screen: Screen.UserProfile,
  },
)
